import { useMemo, useState } from "react";
import { ApiFactory } from "../../core/config/ApiFactory";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { StatusPedido } from "../enum/StatusPedido";
import { TipoPedido } from "../enum/TipoPedido";
import { IPedido } from "../interface/IPedido";
import { IProdutoPedido } from "../interface/IProdutoPedido";
import { TipoAnexoPedido } from "../enum/TipoAnexoPedido";
import { MetodoPagamento } from "../../core/enum/MetodoPagamento";
import { IEndereco } from "../../core/interfaces/IEndereco";
import { TipoPessoa } from "../pages/Operacao/containers/EmitirNotaFiscalModal";
import { IDadosEmitirNotaFiscal } from "../interface/IDadosEmitirNotaFiscal";
import { IDadosEmitirNFe } from "../interface/IDadosEmitirNFe";
import { IFiltrosPedidos } from "../interface/IFiltrosPedidos";
import { IPagamentoPedido } from "../interface/IPagamentoPedido";
import { TipoMarketplace } from "../../marketplace/enum/TipoMarketplace";
import { ICancelamentoPedido } from "../interface/ICancelamentoPedido";
import { IRespostaNegociacaoPedido } from "../interface/IRespostaNegociacaoPedido";
import { IEntregador } from "../../entregadores/interfaces/IEntregador";
import { ICliente } from "../../clientes/interface/ICliente";

/** Cobre o polling da autorização da NF-e na SEFAZ, que pode passar de 40s. */
const TIMEOUT_EMISSAO_NFE_MS = 120000;

export const usePedidos = () => {
    const apiPedidos = useMemo(() => ApiFactory.getApi("MS_PEDIDOS"), []);
    const snackbar = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [loadingAnexo, setLoadingAnexo] = useState(false);

    const gerarPedido = async (pedido: IPedido, tipo: TipoPedido) => {
        if(loading) {
            return null;
        }
        
        if(tipo == TipoPedido.delivery) {
            if(!pedido.cliente) {
                return snackbar("Cliente indefinido", { severity: "error" })
            }

            if(!pedido.endereco) {
                return snackbar("Endereço indefinido", { severity: "error" })
            }
        }

        if(tipo == TipoPedido.retirada) {
            if(!pedido.cliente) {
                return snackbar("Cliente indefinido", { severity: "error" })
            }
        }

        if(!pedido?.marketplace) {
            pedido.marketplace = {
                tipo: TipoMarketplace.PDV
            }
        }

        setLoading(true);
        try {
            pedido.produtos = pedido.produtos?.map(produto => {
                delete produto.sabores;
                delete produto.borda;
                delete produto.complementos;
                return produto;
            })

            const { data } = await apiPedidos.post("/", pedido);
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const dividirPedido = async (pedidoUuid: string, produtos: string[]) => {
        if(loading) {
            return null;
        }
        
        setLoading(true);
        try {
            const { data } = await apiPedidos.post(`/${pedidoUuid}/dividir`, { produtos });
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const listarPedidos = async (filtros: IFiltrosPedidos) => {
        setLoading(true);

        const params = new URLSearchParams();
        Object.entries(filtros).forEach(([key, value]) => {
            if (value) { 
                params.append(key, value instanceof Date ? value.toISOString() : value.toString());
            }
        });
        const queryString = params.toString();    
        
        try {
            const { data } = await apiPedidos.get(`/?${queryString}`);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao listar os pedidos", { severity: "error" })
            return [];
        } finally {
            setLoading(false);            
        }
    }

    const exportarPedidos = async (filtros: IFiltrosPedidos) => {
        const params = new URLSearchParams();
        Object.entries(filtros).forEach(([key, value]) => {
            if (value) {
                params.append(key, value instanceof Date ? value.toISOString() : value.toString());
            }
        });

        try {
            const { data } = await apiPedidos.get(`/relatorios/pedidos/exportar?${params.toString()}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(data);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `pedidos-${filtros.dataInicio ?? filtros.agendadoParaInicio}-a-${filtros.dataFim ?? filtros.agendadoParaFim}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar os pedidos", { severity: "error" })
        }
    }

    const buscarPedido = async (pedidoUuid: string) => {
        if(loading) {
            return null;
        }

        if(!pedidoUuid) {
            snackbar("Pedido inválido", { severity: "error" })
            return null;
        }

        setLoading(true);

        try {
            const { data } = await apiPedidos.get(`/${pedidoUuid}`);
            return data;
        } catch (error) {
            snackbar("Pedido não encontrado", { severity: "error" })
            return null;
        } finally {
            setLoading(false);            
        }
    }

    const alterarEndereco = async (pedidoUuid: string, endereco?: IEndereco) => {
        if(loading) {
            return false;
        }

        if(!pedidoUuid) {
            snackbar("Pedido inválido", { severity: "error" })
            return false;
        }

        setLoading(true);
        try {
            const { data } = await apiPedidos.patch(`/${pedidoUuid}/endereco`, endereco);
            if(data) {
                return true;
            }
        } catch (error) {
            return false
        } finally {
            setLoading(false)
        }
    }

    const alterarTipo = async (pedidoUuid: string, tipo: TipoPedido, endereco?: IEndereco) => {
        if(loading) {
            return false;
        }

        if(!pedidoUuid) {
            snackbar("Pedido inválido", { severity: "error" })
            return false;
        }

        if(!tipo) {
            snackbar("Tipo inválido", { severity: "error" })
            return false;
        }

        setLoading(true);
        try {
            const { data } = await apiPedidos.patch(`/${pedidoUuid}/tipo`, { endereco, tipo });
            if(data) {
                return true;
            }
        } catch (error) {
            return false
        } finally {
            setLoading(false)
        }
    }

    const gerarAnexo = async (pedidoUuid: string, tipo: TipoAnexoPedido, configuracaoUuid: string, produtos?: IProdutoPedido[]) => {
        try {
            setLoadingAnexo(true);
            const { data } = await apiPedidos.post(`/${pedidoUuid}/anexos/${tipo}/${configuracaoUuid}`, { produtos });
            if(data) {
                return true;
            }
        } catch (error) {
            return false;
        } finally {
            setLoadingAnexo(false);
        }
    }

    const atualizarStatus = async ({
        pedidoUuid,
        status,
        cancelamento,
        pagamentos,
        respostaNegociacao,
        entregador
    }: {
        pedidoUuid: string, 
        status: StatusPedido | string, 
        pagamentos?: IPagamentoPedido[],
        cancelamento?: ICancelamentoPedido,
        respostaNegociacao?: IRespostaNegociacaoPedido,
        entregador?: IEntregador
    }) => {
        setLoading(true);
        try {
            const { data } = await apiPedidos.patch(`/${pedidoUuid}/status/${status}`, { 
                pagamentos, 
                cancelamento,
                respostaNegociacao,
                entregador
            });
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const alterarTaxaServico = async (pedidoUuid: string, percentual?: number) => {
        setLoading(true);
        try {
            const { data } = await apiPedidos.patch(`/${pedidoUuid}/taxa-servico`, { percentual });
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const atualizarPagamentos = async (pedidoUuid: string, pagamentos: IPagamentoPedido[]) => {
        setLoading(true);
        try {
            const { data } = await apiPedidos.patch(`/${pedidoUuid}/pagamentos`, { pagamentos });
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const adicionarProduto = async (produto: IProdutoPedido, pedidoUuid: string) => {
        setLoading(true);
        try {
            delete produto.sabores;
            delete produto.borda;
            delete produto.complementos;
            
            const { data } = await apiPedidos.post(`/${pedidoUuid}/produto`, produto);
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const removerProduto = async (produtoUuid: string, pedidoUuid: string, quantidade?: number) => {
        setLoading(true);
        try {
            const { data } = await apiPedidos.delete(`/${pedidoUuid}/produto/${produtoUuid}/${quantidade ?? 1}`);
            return data;
        } catch (error) {
            return null;
        } finally {
            setLoading(false);
        }
    }

    const emitirNotaFiscal = async (
        pedidoUuid: string,
        dados?: IDadosEmitirNotaFiscal
    ) => {
        setLoadingAnexo(true);
        try {
            const { data } = await apiPedidos.post(`/${pedidoUuid}/anexos/nfce`, dados);
            if(data) {
                return true;
            }
        } catch (error) {
            return false;
        } finally {
            setLoadingAnexo(false);
        }
    }

    /**
     * A NF-e é autorizada de forma assíncrona pela SEFAZ e o backend aguarda esse retorno,
     * por isso o timeout é maior que o padrão da API.
     */
    const emitirNFe = async (pedidoUuid: string, dados: IDadosEmitirNFe) => {
        setLoadingAnexo(true);
        try {
            const { data } = await apiPedidos.post(`/${pedidoUuid}/anexos/nfe`, dados, {
                timeout: TIMEOUT_EMISSAO_NFE_MS
            });

            if(data) {
                return true;
            }

            return false;
        } catch (error: any) {
            const mensagem = error?.response?.data?.erros?.[0]
                ?? error?.response?.data?.mensagem
                ?? "Ocorreu um erro ao emitir a NF-e";
            snackbar(mensagem, { severity: "error" });
            return false;
        } finally {
            setLoadingAnexo(false);
        }
    }

    const vincularCliente = async (pedidoUuid: string, cliente: ICliente) => {
        if(loading) {
            return false;
        }

        if(!pedidoUuid) {
            snackbar("Pedido inválido", { severity: "error" })
            return false;
        }

        if(!cliente || !cliente.uuid) {
            snackbar("Cliente inválido", { severity: "error" })
            return false;
        }

        setLoading(true);
        try {
            const { data } = await apiPedidos.patch(`/${pedidoUuid}/cliente`, { cliente });
            if(data) {
                return true;
            }
        } catch (error) {
            return false;
        } finally {
            setLoading(false);
        }
    }

    const desvincularCliente = async (pedidoUuid: string) => {
        if(loading) {
            return false;
        }

        if(!pedidoUuid) {
            snackbar("Pedido inválido", { severity: "error" })
            return false;
        }

        setLoading(true);
        try {
            const { data } = await apiPedidos.delete(`/${pedidoUuid}/cliente`);
            if(data) {
                return true;
            }
        } catch (error) {
            return false;
        } finally {
            setLoading(false);
        }
    }

    return {
        loading,
        gerarPedido,
        atualizarStatus,
        adicionarProduto,
        removerProduto,
        gerarAnexo,
        atualizarPagamentos,
        alterarTaxaServico,
        alterarEndereco,
        emitirNotaFiscal,
        emitirNFe,
        buscarPedido,
        listarPedidos,
        exportarPedidos,
        alterarTipo,
        loadingAnexo,
        dividirPedido,
        vincularCliente,
        desvincularCliente
    }
}